import React, { useState, useEffect } from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonChip,
  IonLabel,
  IonSpinner,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { collectionGroup, onSnapshot, query, where } from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../../firebase";
import { SchedJobData } from "../../hooks/scheduler/useJobData";
import { SchedDetailsComponent } from "../ShiftDetailsSchedComponents/Details";
import { SchedWerkersComponent } from "../ShiftDetailsSchedComponents/Werkers";

interface AdminJobViewerModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  uniqueJobId: string;
  schedUid: string;
  viewType: "werker" | "scheduler";
  werkerUid?: string; // Required when viewType is "werker"
}

export const AdminJobViewerModal: React.FC<AdminJobViewerModalProps> = ({
  isOpen,
  onDismiss,
  uniqueJobId,
  schedUid,
  viewType,
  werkerUid,
}) => {
  const [schedJob, setSchedJob] = useState<SchedJobData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !uniqueJobId || !schedUid) {
      setSchedJob(null);
      return;
    }

    // For werker view, we need both werkerUid and schedUid
    if (viewType === "werker" && !werkerUid) {
      console.error("werkerUid is required for werker viewType");
      setSchedJob(null);
      return;
    }

    setLoading(true);

    // Query differently based on viewType
    let q;
    if (viewType === "scheduler") {
      // Scheduler view: query scheduler's own job doc (userInfoFrom == userInfoTo == schedUid)
      q = query(
        collectionGroup(db, "jobsDocs"),
        where("jobInfo.core.uniqueJobId", "==", uniqueJobId),
        where("docStamp.userInfoFrom.uid", "==", schedUid),
        where("docStamp.userInfoTo.uid", "==", schedUid)
      );
    } else {
      // Werker view: query werker's assigned job doc (userInfoTo == werkerUid, userInfoFrom == schedUid)
      q = query(
        collectionGroup(db, "jobsDocs"),
        where("jobInfo.core.uniqueJobId", "==", uniqueJobId),
        where("docStamp.userInfoFrom.uid", "==", schedUid),
        where("docStamp.userInfoTo.uid", "==", werkerUid)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const jobDoc: any = snapshot.docs[0].data();
        const data: SchedJobData = {
          JobDocId: jobDoc.jobInfo?.core?.uniqueJobId || uniqueJobId,
          ShiftIdentifier: jobDoc.jobInfo?.core?.shiftIdentifier || "",
          SchedUid: jobDoc.docStamp?.userInfoFrom?.uid || schedUid,
          DateDay: jobDoc.jobInfo?.core?.dateTimeStart
            ? format(new Date(jobDoc.jobInfo.core.dateTimeStart.substring(0, 19)), "M/d/yy")
            : "",
          EndDay: jobDoc.jobInfo?.core?.dateTimeFinish
            ? format(new Date(jobDoc.jobInfo.core.dateTimeFinish.substring(0, 19)), "M/d/yy")
            : "",
          StartDateTime: jobDoc.jobInfo?.core?.dateTimeStart
            ? format(new Date(jobDoc.jobInfo.core.dateTimeStart.substring(0, 19)), "h:mma")
            : "",
          FinishDateTime: jobDoc.jobInfo?.core?.dateTimeFinish
            ? format(new Date(jobDoc.jobInfo.core.dateTimeFinish.substring(0, 19)), "h:mma")
            : "",
          StartDateTimeRaw: jobDoc.jobInfo?.core?.dateTimeStart || "",
          FinishDateTimeRaw: jobDoc.jobInfo?.core?.dateTimeFinish || "",
          Company: jobDoc.jobInfo?.core?.company || "",
          Location: jobDoc.jobInfo?.core?.location || "",
          LocationCoordinates: jobDoc.jobInfo?.core?.locationCoordinates || "",
          Pay: jobDoc.jobInfo?.core?.pay || "",
          ShiftNotes: jobDoc.jobInfo?.core?.shiftNotes || "",
          NumJobSlots: jobDoc.jobInfo?.core?.numJobSlots || 0,
          JobStatusSched: jobDoc.jobInfo?.status?.jobStatusSched || "",
          JobStatusWerker: jobDoc.jobInfo?.status?.jobStatusWerker || "",
          IsPaidJob: jobDoc.jobInfo?.status?.isPaidJob || false,
          PaidWerker: jobDoc.jobInfo?.status?.isPaidWerker || false,
          PaidWerkers: jobDoc.jobInfo?.status?.isPaidWerkers || false,
          JobCancelledByScheduler: jobDoc.jobInfo?.status?.jobCancelledByScheduler || false,
          CreatedAt: jobDoc.docStamp?.timestamp?.createdAt || 0,
          UpdatedAt: jobDoc.docStamp?.timestamp?.updatedAt || 0,
          IsRead: jobDoc.jobInfo?.status?.isRead || false,
          IsNewMessage: jobDoc.jobInfo?.status?.isNewMessage || false,
          scheduledCount: jobDoc.jobInfo?.status?.scheduledCount ?? 0,
          seriesId: jobDoc.jobInfo?.core?.seriesId ?? null,
          seriesIndex: jobDoc.jobInfo?.core?.seriesIndex ?? null,
          seriesTotal: jobDoc.jobInfo?.core?.seriesTotal ?? null,
        };
        setSchedJob(data);
      } else {
        setSchedJob(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isOpen, uniqueJobId, schedUid, viewType, werkerUid]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Job Details</IonTitle>
          <IonButtons slot="start">
            <IonChip outline color={viewType === "scheduler" ? "warning" : "primary"} style={{ margin: "0 8px" }}>
              <IonLabel style={{ fontSize: "0.75rem" }}>
                {viewType === "scheduler" ? "Reported as Scheduler" : "Reported as Werker"}
              </IonLabel>
            </IonChip>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={onDismiss}>
              <IonIcon icon={closeOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50%",
            }}
          >
            <IonSpinner name="crescent" />
          </div>
        )}

        {!loading && !schedJob && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "var(--ion-color-medium)",
            }}
          >
            <p>Job not found. The job may have been deleted or the ID is invalid.</p>
          </div>
        )}

        {!loading && schedJob && (
          <IonGrid>
            <SchedDetailsComponent
              jobDocId={uniqueJobId}
              schedJob={schedJob}
              onEdit={() => {}}
              onClone={() => {}}
              onDelete={() => {}}
            />
            {viewType === "scheduler" && (
              <SchedWerkersComponent jobDocId={uniqueJobId} seriesId={schedJob.seriesId} />
            )}
          </IonGrid>
        )}
      </IonContent>
    </IonModal>
  );
};
